import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { getRatingByRange } from '@risksmart-app/i18n/src/ratings';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';
import { hasColor, hasLikelihoodImpact, hasRange } from '@/utils/utils';

import { bandRatingCell } from './bandedRatingLookups';
import { useScoringSettings } from './useScoringSettings';

/**
 * Calculated the risk assessment rating based on impact and likelihood.
 * @param controlType
 * @param configType
 * @returns
 */
export const useCalculateRiskRating = (
  controlType: Risk_Assessment_Result_Control_Type_Enum | undefined,
  configType: ConfigType = 'risk_assessment'
) => {
  const { hasScoringSettings, getRatingByLikelihoodAndImpact } =
    useScoringSettings({ configType });
  const { options } = useRating(
    controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
      ? 'risk_controlled'
      : 'risk_uncontrolled'
  );

  return ({
    likelihood,
    impact,
  }: {
    likelihood: number;
    impact: number;
  }):
    | {
        value: number;
        label: string;
        color?: string;
      }
    | undefined => {
    if (hasScoringSettings) {
      const cell = getRatingByLikelihoodAndImpact(likelihood, impact);
      if (cell) {
        return { label: cell.label, value: cell.value, color: cell.color };
      }

      return undefined;
    }

    // Taxonomy options that declare cells resolve through the shared band rule
    // (RSP-7284), exactly as the matrix config does; legacy options declaring
    // only a score range are matched on the combined score. Both are the shared
    // i18n rules — this hook no longer carries a resolver of its own.
    const declaredCells = options.filter(hasLikelihoodImpact);
    const cell = bandRatingCell(declaredCells, likelihood, impact);

    // A taxonomy that declares cells is a matrix methodology, so a coordinate
    // it does not cover is a gap — not something to answer from a score range.
    // The server holds the same line (RSP-6195, `resolve-matrix-rating.ts`).
    // Without this, a taxonomy mixing cells with legacy range-only options
    // answers a gapped cell — and the unassessed `(0, 0)` pair, which
    // `Math.max(…, 1)` lifts to a combined score of 1 — from whichever range
    // happens to cover it, which is how RSP-5599 first surfaced.
    if (cell.status !== 'resolved' && declaredCells.length > 0) {
      return undefined;
    }

    const rating =
      cell.status === 'resolved'
        ? cell.rating
        : getRatingByRange(
            options.filter(
              (option) => !hasLikelihoodImpact(option) && hasRange(option)
            ),
            Math.max(likelihood, 1) * Math.max(impact, 1) // Don't times by 0
          );

    if (!rating) {
      // Don't log an error here, this function is called frequently (e.g. when resizing columns in the register)
      //  and it will cause performance issues and unnecessary noise in Sentry.
      return undefined;
    }

    return {
      label: rating.label || 'Unknown',
      value: Number(rating.value) || 0,
      color: hasColor(rating) ? rating.color : undefined,
    };
  };
};
